import { setter }                                          from 'mobx-decorators'
import moment                                              from 'moment'

import xhr                                                 from 'helpers/XHR'
import getError                                            from 'helpers/ErrorParser'

import uiStore                                             from 'stores/UiStore'
import Pagination                                          from 'stores/models/Pagination'
import Communication                                       from 'stores/models/Communication'
import Contact                                             from 'stores/models/Contact'

import { observable, action, computed, autorun, reaction } from 'mobx'

import { pipe, map, orderBy, reduce, filter }              from 'lodash/fp'

export const TYPE_FILTERS = {
  ALL:      'All',
  CALLS:    'Calls',
  MESSAGES: 'Messages',
  EMAILS:   'Emails',
  VIDEO:    'Video'
}

export const EMPTY_CHOICE = 'NONE'

export class CommsStore {
  @setter @observable student = null
  @setter @observable isFetchingComms = false
  @setter @observable isError = false
  @setter @observable isFetchingContacts = false
  @setter @observable isFetchingCommsReport = false
  @observable communications = []
  @setter @observable selectedComm = null
  @observable pagination = new Pagination(this, 25)
  @setter @observable filter = TYPE_FILTERS.ALL
  @observable contacts = observable.map()
  @observable contactFilter = ''
  @observable intervalHandler = null

  constructor() {
    this.initAutoruns()
  }

  @computed get sortedCommunications() {
    return orderBy((c) => c.createdAt, ['desc'])(this.communications)
  }

  @computed get isEmpty() {
    return !this.communications.length
  }

  @computed get studentId() {
    return this.student.id
  }

  /** Pagination **/
  @computed get paginationParams() {
    return {
      page:  this.pagination.current,
      limit: this.pagination.pageSize
    }
  }

  @computed get showPagination() {
    return (
      !this.isFetchingComms &&
      !this.isEmpty &&
      this.pagination.showPagination
    )
  }

  @computed get filterParams() {
    const params = {
      type: this.getContactTypeFromFilter(this.filter)
    }

    if (this.contactFilter && this.contactFilter !== EMPTY_CHOICE) {
      params.contact = this.contactFilter
    }

    return params
  }

  @action getContactTypeFromFilter = (filter) => {
    switch (filter) {
    case TYPE_FILTERS.CALLS:
      return 'call'
    case TYPE_FILTERS.MESSAGES:
      return 'sms'
    case TYPE_FILTERS.EMAILS:
      return 'email'
    case TYPE_FILTERS.VIDEO:
      return 'video'
    default:
      return null
    }
  }

  @action onPageChange = () => {
    this.fetchCommunicationHistory()
  }

  @action setPagination = ({ total }) => {
    this.pagination.setTotal(parseInt(total))
    this.pagination.calculateTotalResults()
  }

  getCommHistoryParams = () => ({
    params: {
      only: [
        'id',
        'action',
        'created_at',
        'type',
        'preview',
        'link_ref',
        'direction',
        'media_url',
        'length',
        'user.id',
        'user.username',
        'user.first_name',
        'user.last_name',
        'explicit_content',
        'contact.id',
        'contact.name',
        'contact.relationship',
        'contact.email',
        'contact.phone',
        'call_status',
        'voicemail_url',
        'language',
        'meta',
        'contact.student.full_name',
        'broadcast_id',
        'notes'
      ].join(','),
      ...this.paginationParams,
      ...this.filterParams
    }
  })

  isCommEqualToSelectedComm = (communication) =>
    communication.contact.id === this.selectedComm.contact.id &&
    communication.user.id === this.selectedComm.user.id

  @computed get groupedSms() {
    return filter(
      (c) => c.type === 'sms' && this.isCommEqualToSelectedComm(c)
    )(this.communications)
  }

  @computed get groupedEmails() {
    return filter(
      (c) => c.type === 'email' && this.isCommEqualToSelectedComm(c)
    )(this.communications)
  }

  getDate = (date) => {
    return moment(date).calendar(null, {
      lastDay:  '[Yesterday]',
      sameDay:  '[Today]',
      nextDay:  '[Tomorrow]',
      lastWeek: '[last] dddd',
      nextWeek: 'dddd',
      sameElse: 'L'
    })
  }

  /*
   * returns @ { groupDate: [sms1, sms2] }
   * eg. {'Today':[{sms1}, {sms2}]}
   */
  @computed get orderedSms() {
    return pipe(
      map((s) => {
        s['groupDate'] = this.getDate(s.createdAt)
        return s
      }),
      orderBy(['createdAt'], ['asc']),
      reduce((acc, curr) => {
        if (!acc[curr.groupDate]) {
          acc[curr.groupDate] = []
        }

        acc[curr.groupDate].push(curr)
        return acc
      }, {})
    )(this.groupedSms)
  }

  /*
   * returns @ { groupDate: [email1, email2] }
   * eg. {'Today':[{email1}, {email2}]}
   */
  @computed get orderedEmails() {
    return pipe(
      map((s) => {
        s['groupDate'] = this.getDate(s.createdAt)
        return s
      }),
      orderBy(['createdAt'], ['asc']),
      reduce((acc, curr) => {
        if (!acc[curr.groupDate]) {
          acc[curr.groupDate] = []
        }

        acc[curr.groupDate].push(curr)
        return acc
      }, {})
    )(this.groupedEmails)
  }

  @action init = (student) => {
    this.setStudent(student)
    this.clearFetchingCommsReport()
    this.fetchCommunicationHistory()
    this.fetchStudentContacts()
  }

  @action onUnmount = () => {
    this.dispose()
    this.clearPagination()
    this.clearData()
    this.clearContacts()
    this.clearFilters()
  }

  /*
   * Autoruns
   */
  @action initAutoruns = () => {
    this.autoErrorNotifier()
    this.autoFetchOnContactFilterChange()
    this.autoFetchOnTypeFilterChange()
  }

  @action autoFetchOnTypeFilterChange = () => {
    this.autoFetchOnTypeFilterChangeDisposer = reaction(
      () => this.filter,
      (isFetch) => !!isFetch && this.fetchCommunicationHistory()
    )
  }

  @action autoFetchOnContactFilterChange = () => {
    this.autoFetchOnContactFilterChangeDisposer = reaction(
      () => this.contactFilter,
      (isFetch) => !!isFetch && this.fetchCommunicationHistory()
    )
  }
  @action autoErrorNotifier = () => {
    this.autoErrorDisposer = autorun('Watch errors', () => {
      if (this.isError && !this.isError.hideNotification) {
        uiStore.addNotification({
          title:   this.isError.title,
          message: this.isError.message,
          type:    this.isError.type || 'error'
        })
      }
    })
  }

  @action fetchCommunicationHistory = async() => {
    try {
      this.clearData()
      this.setIsFetchingComms(true)
      this.setIsError(false)

      const { headers, data } = await xhr.get(
        `/channel/communications/${this.studentId}`,
        this.getCommHistoryParams()
      )

      this.fetchCommunicationHistoryOK(data, headers)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsFetchingComms(false)
    }
  }

  @action fetchCommunicationHistoryOK = (data = [], headers) => {
    this.clearData()
    this.setPagination(headers)
    data.forEach(this.createCommunication)
  }

  @action createCommunication = (comm) => {
    this.communications.push(new Communication(this, comm))
  }

  @action dispose = () => {
    this.communications.forEach((c) => c.dispose())
  }

  @action clearPagination = () => {
    this.pagination.clear()
  }

  @action handleOnFilterChange = (e) => {
    this.setFilter(e.target.value)
    this.clearPagination()
  }

  @action fetchStudentContacts = async() => {
    try {
      this.setIsFetchingContacts(true)
      this.setIsError(false)

      this.contacts.clear()

      const response = await xhr.get(
        `/students/${this.studentId}/contacts`,
        {
          params: {
            only: ['id', 'student_id', 'name', 'phone'].join(',')
          }
        }
      )

      this.fetchStudentContactsOK(response)
    } catch (e) {
      this.setIsError(getError(e))
    } finally {
      this.setIsFetchingContacts(false)
    }
  }

  @action fetchStudentContactsOK = (response) => {
    response.data.forEach(this.createContact)
  }
  @action createContact = (json) => {
    this.contacts.set(json.id, new Contact(this, json))
  }

  @action onContactChange = (id = EMPTY_CHOICE) => {
    this.contactFilter = id
    this.clearPagination()
  }

  /*
   * Clearing data
   */
  @action setDefaultTypeFilter = () => {
    this.setFilter(TYPE_FILTERS.ALL)
  }

  @action clearFilters = () => {
    this.setDefaultTypeFilter()
    this.contactFilter = ''
  }

  @action clearContacts = () => {
    this.contacts.clear()
  }

  @action clearData = () => {
    this.communications.clear()
    this.selectedComm = null
    this.clearFetchingCommsReport()
  }

  @action handleOnReportClick = async() => {
    try {
      this.setIsFetchingCommsReport(true)
      const res = await xhr.get(
        `/comms_report/student_report/${this.studentId}`,
        {}
      )
      const jid = res.data

      this.initStatusTimer(jid)
    } catch (e) {
      this.setIsError(getError(e))
      this.setIsFetchingCommsReport(false)
    }
  }

  @action initStatusTimer = (jid) => {
    this.intervalHandler = setInterval(
      () => this.getReportStatus(jid),
      5000
    )
  }

  @action getReportStatus = async(jid) => {
    const res = await xhr.get(
      `/comms_report/student_report/${this.studentId}/${jid}`,
      {}
    )

    if (res.data !== null) {
      this.clearFetchingCommsReport()

      window.location.href = res.data
    }
  }

  @action clearFetchingCommsReport = () => {
    clearInterval(this.intervalHandler)
    this.intervalHandler = null
    this.setIsFetchingCommsReport(false)
  }
}

export default new CommsStore()
